import {
  IconAdjustments,
  IconBell,
  IconBolt,
  IconBrandWhatsapp,
  IconBuilding,
  IconCreditCard,
  IconHeadset,
  IconMessageCircle,
  IconDatabase,
  IconTag,
  IconUser,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import { RoutePaths } from "@/core/constants/RoutePaths";

/** De quem é a configuração: da conta (setor), da pessoa ou da empresa. Decide o banner. */
export type SettingsScope = "account" | "personal" | "company";

export type SettingsPath =
  | typeof RoutePaths.SETTINGS_ATTENDINGS
  | typeof RoutePaths.SETTINGS_WHATSAPP
  | typeof RoutePaths.SETTINGS_TAGS
  | typeof RoutePaths.SETTINGS_ADVANCED
  | typeof RoutePaths.SETTINGS_NOTIFICATIONS
  | typeof RoutePaths.SETTINGS_QUICK_REPLIES
  | typeof RoutePaths.SETTINGS_BILLING
  | typeof RoutePaths.SETTINGS_DATA_RETENTION;

export type SettingsNavItem = {
  path: SettingsPath;
  label: string;
  icon: TablerIcon;
  scope: SettingsScope;
};

export type SettingsNavGroup = {
  scope: SettingsScope;
  label: string;
  icon: TablerIcon;
  items: SettingsNavItem[];
};

/** Sub-nav do `Atendimento.dc.html` (L880): grupos e itens na ordem do design. */
export const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    scope: "account",
    label: "Esta conta",
    icon: IconMessageCircle,
    items: [
      {
        path: RoutePaths.SETTINGS_ATTENDINGS,
        label: "Atendimentos",
        icon: IconHeadset,
        scope: "account",
      },
      {
        path: RoutePaths.SETTINGS_WHATSAPP,
        label: "WhatsApp",
        icon: IconBrandWhatsapp,
        scope: "account",
      },
      {
        path: RoutePaths.SETTINGS_TAGS,
        label: "Tags",
        icon: IconTag,
        scope: "account",
      },
      {
        path: RoutePaths.SETTINGS_ADVANCED,
        label: "Avançado",
        icon: IconAdjustments,
        scope: "account",
      },
    ],
  },
  {
    scope: "personal",
    label: "Você",
    icon: IconUser,
    items: [
      {
        path: RoutePaths.SETTINGS_NOTIFICATIONS,
        label: "Notificações",
        icon: IconBell,
        scope: "personal",
      },
      {
        path: RoutePaths.SETTINGS_QUICK_REPLIES,
        label: "Respostas rápidas",
        icon: IconBolt,
        scope: "personal",
      },
    ],
  },
  {
    scope: "company",
    label: "Empresa",
    icon: IconBuilding,
    items: [
      {
        path: RoutePaths.SETTINGS_BILLING,
        label: "Faturamento",
        icon: IconCreditCard,
        scope: "company",
      },
      // SA-1433: retenção de mídia, admin da empresa (mesma regra de Faturamento).
      {
        path: RoutePaths.SETTINGS_DATA_RETENTION,
        label: "Retenção de dados",
        icon: IconDatabase,
        scope: "company",
      },
    ],
  },
];

/** Preferências da pessoa: abrem para qualquer perfil (SA-1348, SA-1426). */
export const PERSONAL_SETTINGS_PATHS: SettingsPath[] = [
  RoutePaths.SETTINGS_NOTIFICATIONS,
  RoutePaths.SETTINGS_QUICK_REPLIES,
];

/**
 * O que cada um vê: "Esta conta" exige `ui:settings:view` no setor ativo,
 * "Empresa" exige ser admin da empresa, "Você" é de todo mundo. Item sem acesso
 * some (não aparece com cadeado) e grupo vazio some com o cabeçalho.
 */
export function visibleSettingsNav({
  canViewSettings,
  isCompanyAdmin,
}: {
  canViewSettings: boolean;
  isCompanyAdmin: boolean;
}): SettingsNavGroup[] {
  const allowed: Record<SettingsScope, boolean> = {
    account: canViewSettings,
    personal: true,
    company: isCompanyAdmin,
  };
  return SETTINGS_NAV.filter(
    (group) => allowed[group.scope] && group.items.length > 0
  );
}

export function findSettingsItem(
  pathname: string
): SettingsNavItem | undefined {
  return SETTINGS_NAV.flatMap((group) => group.items).find(
    (item) => pathname === item.path || pathname.startsWith(`${item.path}/`)
  );
}
